import { memo, useMemo } from 'react';
import { create } from 'zustand';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Line, Text } from '@react-three/drei';
import { Entity, useAppStore, useSelectedIdSet, selectEntitiesForView } from '../state/store';
import { ArcPreview, PolygonPreview, PolylinePreview, EllipsePreview } from '../tools/cad/ExtendedDraftingTools';
import { TextEntity, InPlaceTextEditor, LinearDimensionRenderer, RadialDimensionRenderer, type LinearDimensionData, type RadialDimensionData, type TextData } from '../tools/cad';
import {
    AngularDimensionRenderer, ArcLengthDimensionRenderer, OrdinateDimensionRenderer,
    JoggedDimensionRenderer, ToleranceFrameRenderer,
} from '../tools/cad/SpecialDimensionRenderers';
import { InfiniteLineEntity } from './InfiniteLineEntity';
import {
    DoorMesh, WindowMesh, FloorMesh, ColumnMesh, RoofMesh, CeilingMesh,
    StairMesh, RailingMesh, RampMesh, ComponentMesh, AreaMesh, ToposolidMesh,
    WallMesh,
    type DoorData, type WindowData, type FloorData, type ColumnData, type RoofData,
    type CeilingData, type StairData, type RailingData, type RampData, type ComponentData,
    type AreaData, type ToposolidData
} from '../tools/bim';
import { LoftMesh, ExtrudeMesh, SweepMesh, RevolveMesh, type LoftData, type ExtrudeData, type SweepData, type RevolveData } from '../tools/nurbs';
import { dispatchEntityClick, useEntityRegistryRef, useIsPreviewViewport } from './toolEventBus';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { INSTANCE_THRESHOLD, InstancedEntityGroup, InstancedEntityType } from './instancing/InstancedEntityGroup';
import { SolidEntityMesh } from './SolidEntityMesh';
import { BlockRefMesh } from './BlockRefMesh';
import { NurbsCurveEntity, NurbsSurfaceEntity, NurbsPolysurfEntity } from './NurbsKernelEntities';
import { SubdEntityMesh } from './SubdEntityMesh';
import { interpolateCurve, sampleCurve } from '../core/geometry/nurbs';
import { expandBulgePolyline } from '../core/commands/geometryMath';
import { useSystemVarsStore } from '../state/useSystemVarsStore';
import { entityLineStyle, lineStyleProps } from './entityLineStyle';
import type { Layer } from '../state/store';

/**
 * Entity Layer (Phase 2 decomposition, tasks 2.1-2.3 + Phase 3 instancing)
 *
 * - Repetitive primitive bulk (box/sphere/cylinder) is drawn with one
 *   InstancedMesh per type (single draw call), with per-instance selection
 *   highlighting via instanceColor.
 * - Everything else renders through memoized per-element components that
 *   subscribe only to their own store slices, so a single-element edit or a
 *   selection change re-renders only the affected elements.
 *
 * `cad2D` (2D workbench): lightweight 2D drafting — no instanced bulk,
 * primitives render as wireframe, BIM/solid entities are skipped, and the
 * shaded ground plane is omitted (the container's `bg.cad` shows through).
 */
export const EntityLayer = memo(function EntityLayer({ cad2D = false, viewId = null, showGroundPlane = true }: { cad2D?: boolean; viewId?: string | null; showGroundPlane?: boolean }) {
    const rawEntities = useAppStore((state) => state.entities);
    // When a 2D drawing view is active, only entities tagged with that
    // viewId render (the view's own drafting content); null = model space.
    const entities = useMemo(
        () => (viewId ? selectEntitiesForView(rawEntities, viewId) : rawEntities),
        [rawEntities, viewId]
    );
    const layers = useAppStore((state) => state.layers);
    // Set of layer ids hidden via the Layers panel (Sidebar visibility toggle)
    const hiddenLayerIds = useMemo(() => {
        const hidden = new Set<string>();
        for (const layer of Object.values(layers)) {
            if (!layer.visible) hidden.add(layer.id);
        }
        return hidden;
    }, [layers]);

    const { instancedBatches, individualIds } = useMemo(
        () => partitionEntities(entities, hiddenLayerIds, cad2D),
        [entities, hiddenLayerIds, cad2D]
    );

    return (
        <group>
            {/* Invisible ground plane for raycasting (XY plane, Z-up) */}
            <mesh position={[0, 0, 0]} visible={false}>
                <planeGeometry args={[1000, 1000]} />
                <meshBasicMaterial transparent opacity={0} />
            </mesh>

            {/* Ground plane visual (lit 3D workbenches only; hosts with their
                own ground look — sketch, BIM — suppress it) */}
            {!cad2D && showGroundPlane && (
                <mesh position={[0, 0, -0.01]} receiveShadow>
                    <planeGeometry args={[50, 50]} />
                    <meshStandardMaterial color="#3a3a3a" />
                </mesh>
            )}

            {/* Instanced static bulk: one draw call per primitive type */}
            {instancedBatches.map((batch) => (
                <InstancedEntityGroup key={batch.type} type={batch.type} entities={batch.entities} />
            ))}

            {/* Individually rendered entities (memoized, per-element subscriptions) */}
            {individualIds.map((id) => (
                <EntityRenderer key={id} entityId={id} cad2D={cad2D} />
            ))}
        </group>
    );
});

/**
 * Entity types that only make sense as lit solid geometry (BIM families,
 * NURBS solids, swept/lofted meshes). Skipped entirely in cad2D mode so the
 * 2D workbench stays a pure 2D wireframe drafting surface.
 */
const SOLID_ENTITY_TYPES = new Set([
    'wall', 'door', 'window', 'floor', 'column', 'roof', 'ceiling',
    'stair', 'railing', 'ramp', 'component', 'area', 'toposolid',
    'loft', 'extrude', 'nurbs-surface', 'nurbs-polysurf', 'subd',
]);

/**
 * Splits visible entities into instanced primitive batches and individually
 * rendered ids. Pure function of the entities map. Entities on layers hidden
 * through the Layers panel are excluded from rendering entirely.
 *
 * `cad2D`: no instancing at all (2D drafting never reaches the threshold
 * anyway) and solid BIM/NURBS geometry is dropped; primitives stay and are
 * rendered as wireframe by SingleEntity.
 */
function partitionEntities(
    entities: Record<string, Entity>,
    hiddenLayerIds: Set<string>,
    cad2D: boolean
): {
    instancedBatches: { type: InstancedEntityType; entities: Entity[] }[];
    individualIds: string[];
} {
    const buckets = new Map<InstancedEntityType, Entity[]>();
    const individual: Entity[] = [];

    Object.values(entities).forEach((entity) => {
        if (!entity.visible) return;
        if (hiddenLayerIds.has(entity.layer)) return;
        if (cad2D && SOLID_ENTITY_TYPES.has(entity.type)) return;
        // Paper-space entities (Faz 9) draw through PaperSpaceCadLayer only.
        if (cad2D && entity.data?.onSheet) return;

        if (!cad2D && (entity.type === 'box' || entity.type === 'sphere' || entity.type === 'cylinder')) {
            const type = entity.type as InstancedEntityType;
            const bucket = buckets.get(type);
            if (bucket) bucket.push(entity);
            else buckets.set(type, [entity]);
            return;
        }

        individual.push(entity);
    });

    // DRAWORDER consumption: higher data.renderOrder renders last (on top).
    individual.sort((a, b) => {
        const ra = typeof a.data?.renderOrder === 'number' ? a.data.renderOrder : 0;
        const rb = typeof b.data?.renderOrder === 'number' ? b.data.renderOrder : 0;
        return ra - rb;
    });
    const individualIds = individual.map((e) => e.id);

    const instancedBatches: { type: InstancedEntityType; entities: Entity[] }[] = [];
    buckets.forEach((bucketEntities, type) => {
        if (bucketEntities.length >= INSTANCE_THRESHOLD) {
            instancedBatches.push({ type, entities: bucketEntities });
        } else {
            bucketEntities.forEach((entity) => individualIds.push(entity.id));
        }
    });

    return { instancedBatches, individualIds };
}

// ---- Per-element renderers ----

/**
 * Single entity component. Subscribes to:
 * - its own entity slice (reference-stable for untouched siblings)
 * - selection membership (boolean selector -> re-renders max 2 on change)
 * - hover membership (boolean selector)
 */
const EntityRenderer = memo(function EntityRenderer({ entityId, cad2D = false }: { entityId: string; cad2D?: boolean }) {
    const entity = useAppStore((state) => state.entities[entityId]);
    const isSelected = useAppStore((state) => state.selectedIds.includes(entityId));
    const isHovered = useAppStore((state) => state.hoveredId === entityId);
    const layerVisible = useAppStore((state) => {
        const layer = state.layers[entity?.layer ?? ''];
        return layer ? layer.visible : true;
    });
    // Layer-level BYLAYER style fallback for the entity style resolution.
    const layer = useAppStore((state) => state.layers[entity?.layer ?? '']);
    // HIGHLIGHT sysvar parity: 0 = selected objects keep their normal color.
    const highlightOn = useSystemVarsStore((s) => s.HIGHLIGHT) !== 0;

    if (!entity || !entity.visible || !layerVisible) return null;

    return <SingleEntity entity={entity} isSelected={isSelected && highlightOn} isHovered={isHovered} cad2D={cad2D} layer={layer} />;
});

const highlightClick = (entityId: string) => (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    dispatchEntityClick(entityId, e);
};

/** Which text entity currently shows the in-place DDEDIT editor. */
const useTextEditingStore = create<{ editingTextId: string | null; setEditingTextId: (id: string | null) => void }>((set) => ({
    editingTextId: null,
    setEditingTextId: (editingTextId) => set({ editingTextId }),
}));

const silentClick = (entityId: string) => () => {
    dispatchEntityClick(entityId, {} as any);
};

function SingleEntity({ entity, isSelected, isHovered, cad2D = false, layer }: { entity: Entity; isSelected: boolean; isHovered: boolean; cad2D?: boolean; layer?: Layer }) {
    // Default line ink (theme-driven): white on the dark viewport, black on
    // the light one — applies to every line/text entity without an explicit
    // color, in every view model this layer is mounted in.
    const ink = useAnnotationInk();
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || ink;
    // Graphic override pipeline (docs/plans/2d plan Faz 4): per-entity
    // linetype/lineweight/transparency, layer-level values as fallback.
    const style = entityLineStyle(entity, layer);
    // DDEDIT in-place editor membership (one entity at a time). Preview
    // viewports never host the editor — it is owned by the interactive one.
    const isPreview = useIsPreviewViewport();
    const editingTextId = useTextEditingStore((s) => s.editingTextId);
    const setEditingTextId = useTextEditingStore((s) => s.setEditingTextId);

    const refCallback = useEntityRegistryRef(entity.id);

            // F4-style: tessellate curve entities only when their data changes.
    // Legacy CatmullRom rendering is superseded by the kernel interpolating
    // curve (same through-points, rational B-spline evaluation) so the
    // display matches what the command engine measures everywhere else.
    const curveData = entity.type === 'curve' ? (entity.data.controlPoints as number[][]) : null;
    const curvePoints = useMemo(() => {
        if (!curveData || curveData.length < 2) return [];
        const c = interpolateCurve(curveData.map((p) => [p[0], p[1], p[2] ?? 0] as [number, number, number]));
        if (!c) return curveData.map((p) => new THREE.Vector3(p[0], p[1], p[2] ?? 0));
        return sampleCurve(c, 50).map((p) => new THREE.Vector3(p[0], p[1], p[2] ?? 0));
    }, [curveData]);

    switch (entity.type) {
        // --- PRIMITIVES ---
        case 'box':
            return (
                <mesh
                    key={entity.id}
                    ref={refCallback}
                    position={entity.data.position as [number, number, number] || [0, 0, 0.5]}
                    rotation={entity.data.rotation as [number, number, number] || [0, 0, 0]}
                    onClick={highlightClick(entity.id)}
                    castShadow
                >
                    <boxGeometry args={entity.data.size as [number, number, number] || [1, 1, 1]} />
                    {cad2D ? (
                        <meshBasicMaterial color={color} wireframe />
                    ) : (
                        <meshStandardMaterial color={isSelected || isHovered ? color : '#808080'} />
                    )}
                </mesh>
            );

        case 'sphere':
            return (
                <mesh
                    key={entity.id}
                    ref={refCallback}
                    position={entity.data.position as [number, number, number] || [0, 0, 0.5]}
                    onClick={highlightClick(entity.id)}
                    castShadow
                >
                    <sphereGeometry args={[entity.data.radius as number || 0.5, 32, 32]} />
                    {cad2D ? (
                        <meshBasicMaterial color={color} wireframe />
                    ) : (
                        <meshStandardMaterial color={isSelected || isHovered ? color : '#808080'} />
                    )}
                </mesh>
            );

        case 'cylinder':
            return (
                <mesh
                    key={entity.id}
                    ref={refCallback}
                    // Z-up world: the cylinder axis runs along +Z, so the
                    // geometry (three.js builds it along Y) is laid down onto Z.
                    rotation={[Math.PI / 2, 0, 0]}
                    position={entity.data.position as [number, number, number] || [0, 0, 0.75]}
                    onClick={highlightClick(entity.id)}
                    castShadow
                >
                    <cylinderGeometry args={[
                        entity.data.radiusTop as number || 0.5,
                        entity.data.radiusBottom as number || 0.5,
                        entity.data.height as number || 1.5,
                        32
                    ]} />
                    {cad2D ? (
                        <meshBasicMaterial color={color} wireframe />
                    ) : (
                        <meshStandardMaterial color={isSelected || isHovered ? color : '#808080'} />
                    )}
                </mesh>
            );

        // --- CAD 2D ---
        case 'line': {
            const start = (entity.data.start as [number, number, number]) || [0, 0, 0];
            const end = (entity.data.end as [number, number, number]) || [1, 0, 1];
            // XLINE/RAY render as true construction geometry: the visible
            // span is recomputed from the camera every frame (report 4.4)
            // instead of the stored 500/1000-unit fake segment.
            if (entity.data.xline || entity.data.ray) {
                return (
                    <InfiniteLineEntity
                        key={entity.id}
                        start={start}
                        end={end}
                        ray={!!entity.data.ray}
                        color={color}
                        entityId={entity.id}
                    />
                );
            }
            return (
                <Line
                    key={entity.id}
                    points={[start, end]}
                    color={color}
                    {...lineStyleProps(style)}
                    lineWidth={isSelected ? 3 : style.width}
                    onClick={highlightClick(entity.id)}
                />
            );
        }

        case 'circle': {
            const center = entity.data.center as number[] || [0, 0, 0];
            const radius = entity.data.radius as number || 1;
            const segments = 64;
            const points: [number, number, number][] = [];
            for (let i = 0; i <= segments; i++) {
                const angle = (i / segments) * Math.PI * 2;
                // XY ground plane (Z-up convention)
                points.push([
                    center[0] + Math.cos(angle) * radius,
                    center[1] + Math.sin(angle) * radius,
                    center[2]
                ]);
            }
            return (
                <Line
                    key={entity.id}
                    points={points}
                    color={color}
                    {...lineStyleProps(style)}
                    lineWidth={isSelected ? 3 : style.width}
                    onClick={highlightClick(entity.id)}
                />
            );
        }

        case 'rectangle': {
            const c1 = entity.data.corner1 as number[] || [0, 0, 0];
            const c2 = entity.data.corner2 as number[] || [1, 1, 0];
            // XY ground plane (Z-up convention): corners differ in x/y.
            const points: [number, number, number][] = [
                [c1[0], c1[1], c1[2]],
                [c2[0], c1[1], c1[2]],
                [c2[0], c2[1], c1[2]],
                [c1[0], c2[1], c1[2]],
                [c1[0], c1[1], c1[2]],
            ];
            return (
                <Line
                    key={entity.id}
                    points={points}
                    color={color}
                    {...lineStyleProps(style)}
                    lineWidth={isSelected ? 3 : style.width}
                    onClick={highlightClick(entity.id)}
                />
            );
        }

        case 'arc':
            // Re-use ArcPreview logic or create dedicated renderer
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <ArcPreview arc={entity.data as any} color={color} lineWidth={style.width}
                        dashed={!!style.dash} dashSize={style.dash?.[0]} gapSize={style.dash?.[1]} />
                </group>
            );

        case 'polygon':
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <PolygonPreview
                        center={new THREE.Vector3(...(entity.data.center as number[]))}
                        radius={entity.data.radius as number}
                        sides={entity.data.sides as number}
                        color={color} lineWidth={style.width}
                        dashed={!!style.dash} dashSize={style.dash?.[0]} gapSize={style.dash?.[1]}
                    />
                </group>
            );

        case 'polyline': {
            // Bulge (arc) segments expand to dense arc geometry for display.
            const rawVerts = (entity.data.vertices as number[][]) ?? [];
            const bulges = entity.data.bulges as number[] | undefined;
            const closedFlag = !!entity.data.closed;
            const display = bulges && bulges.some((b) => b)
                ? expandBulgePolyline(rawVerts as [number, number, number][], bulges, closedFlag)
                    .map((v) => new THREE.Vector3(v[0], v[1], v[2] ?? 0))
                : rawVerts.map((v) => new THREE.Vector3(...v));
            // Global polyline width (PEDIT Width): thickens the rendered line.
            const polyWidth = typeof entity.data.width === 'number' && entity.data.width > 0
                ? Math.max(style.width, 1 + entity.data.width * 6)
                : style.width;
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <PolylinePreview
                        vertices={display}
                        previewPoint={null}
                        closed={bulges && bulges.some((b) => b) ? false : closedFlag}
                        color={color} lineWidth={polyWidth}
                        dashed={!!style.dash} dashSize={style.dash?.[0]} gapSize={style.dash?.[1]}
                    />
                </group>
            );
        }

        case 'ellipse':
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <EllipsePreview
                        center={new THREE.Vector3(...(entity.data.center as number[]))}
                        radiusA={entity.data.radiusA as number}
                        radiusB={entity.data.radiusB as number}
                        rotation={entity.data.rotation as number}
                        startAngle={typeof entity.data.startAngle === 'number' ? entity.data.startAngle : undefined}
                        endAngle={typeof entity.data.endAngle === 'number' ? entity.data.endAngle : undefined}
                        color={color} lineWidth={style.width}
                        dashed={!!style.dash} dashSize={style.dash?.[0]} gapSize={style.dash?.[1]}
                    />
                </group>
            );

        case 'point':
            // CAD POINT / 3D _POINT: crosshair marker at its level.
            return (
                <group
                    key={entity.id}
                    ref={refCallback}
                    position={(entity.data.position as [number, number, number]) || [0, 0, 0]}
                    onClick={highlightClick(entity.id)}
                >
                    <Line points={[[-0.1, 0, 0], [0.1, 0, 0]]} color={color} lineWidth={1.5} />
                    <Line points={[[0, -0.1, 0], [0, 0.1, 0]]} color={color} lineWidth={1.5} />
                    <mesh>
                        <sphereGeometry args={[0.03, 8, 8]} />
                        <meshBasicMaterial color={color} />
                    </mesh>
                </group>
            );

        // --- TEXT & DIMENSIONS ---
        case 'text':
        case 'mtext':
            // DDEDIT / TEXTED=0 parity: double-click opens the in-place
            // editor overlay anchored at the text position.
            return (
                <group
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        useAppStore.getState().select([entity.id]);
                        setEditingTextId(entity.id);
                    }}
                >
                    {editingTextId === entity.id && !isPreview
                        ? <InPlaceTextEditor entityId={entity.id} onClose={() => setEditingTextId(null)} />
                        : (
                            <TextEntity
                                key={entity.id}
                                data={entity.data as unknown as TextData}
                                isSelected={isSelected}
                                isHovered={isHovered}
                                onClick={highlightClick(entity.id)}
                            />
                        )}
                </group>
            );

        case 'dimension': {
            // Subtype-true renderers (docs/plans/2d plan A1): angular
            // measures the ANGLE with an arc, arcLength follows the ARC path,
            // ordinate/jogged/tolerance draw their own symbols. baseline/
            // continue stay linear (their geometry IS linear by definition).
            const dsub = String((entity.data as any).type ?? '');
            const subProps = { key: entity.id, data: entity.data as Record<string, any>, isSelected, onClick: highlightClick(entity.id) };
            if (dsub === 'angular') return <AngularDimensionRenderer {...subProps} />;
            if (dsub === 'arcLength') return <ArcLengthDimensionRenderer {...subProps} />;
            if (dsub === 'ordinate') return <OrdinateDimensionRenderer {...subProps} />;
            if (dsub === 'jogged') return <JoggedDimensionRenderer {...subProps} />;
            if (dsub === 'tolerance') return <ToleranceFrameRenderer {...subProps} />;
            if (dsub === 'radius' || dsub === 'diameter') {
                return (
                    <RadialDimensionRenderer
                        key={entity.id}
                        data={entity.data as unknown as RadialDimensionData}
                        isSelected={isSelected}
                        onClick={highlightClick(entity.id)}
                    />
                );
            }
            return (
                <LinearDimensionRenderer
                    key={entity.id}
                    data={entity.data as unknown as LinearDimensionData}
                    isSelected={isSelected}
                    onClick={highlightClick(entity.id)}
                />
            );
        }

        // --- BIM ---
        case 'wall':
            return (
                <WallMesh
                    key={entity.id}
                    data={entity.data}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={highlightClick(entity.id)}
                />
            );

        case 'door':
            return (
                <DoorMesh
                    key={entity.id}
                    data={entity.data as unknown as DoorData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'window':
            return (
                <WindowMesh
                    key={entity.id}
                    data={entity.data as unknown as WindowData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'floor':
            return (
                <FloorMesh
                    key={entity.id}
                    data={entity.data as unknown as FloorData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'column':
            return (
                <ColumnMesh
                    key={entity.id}
                    data={entity.data as unknown as ColumnData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'roof':
            return (
                <RoofMesh
                    key={entity.id}
                    data={entity.data as unknown as RoofData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'ceiling':
            return (
                <CeilingMesh
                    key={entity.id}
                    data={entity.data as unknown as CeilingData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'stair':
            return (
                <StairMesh
                    key={entity.id}
                    data={entity.data as unknown as StairData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'railing':
            return (
                <RailingMesh
                    key={entity.id}
                    data={entity.data as unknown as RailingData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'ramp':
            return (
                <RampMesh
                    key={entity.id}
                    data={entity.data as unknown as RampData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'component':
            return (
                <ComponentMesh
                    key={entity.id}
                    data={entity.data as unknown as ComponentData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'area':
            return (
                <AreaMesh
                    key={entity.id}
                    data={entity.data as unknown as AreaData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        case 'toposolid':
            return (
                <ToposolidMesh
                    key={entity.id}
                    data={entity.data as unknown as ToposolidData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={silentClick(entity.id)}
                />
            );

        // --- BLOCK / ANNOTATION results (command parity D+H waves) ---
        case 'insert':
        case 'block':
            return <BlockRefMesh key={entity.id} entity={entity} isSelected={isSelected} isHovered={isHovered} />;

        // HATCH: boundary + pattern segments (drei Line batches). Solid fills
        // triangulate the boundary; FILLMODE=0 outlines are handled by the
        // runner dropping the segments (here the boundary is always drawn).
        case 'hatch': {
            const boundary = (entity.data.boundary as number[][]) ?? [];
            const segments = (entity.data.segments as number[][]) ?? [];
            if (boundary.length < 3) return null;
            const ring = boundary.map((p) => new THREE.Vector3(p[0], p[1], p[2] ?? 0));
            const fill = entity.data.fill as boolean;
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <Line points={ring} color={color} lineWidth={isSelected ? 2.5 : 1.5} />
                    {segments.length > 0 && segments.map((s, i) => (
                        <Line
                            key={i}
                            points={[[s[0], s[1], s[2]], [s[3], s[4], s[5]]]}
                            color={color}
                            lineWidth={1}
                        />
                    ))}
                    {fill && (
                        <mesh>
                            <shapeGeometry args={[shapeFromPoints(ring)]} />
                            <meshBasicMaterial color={color} transparent opacity={0.35} />
                        </mesh>
                    )}
                </group>
            );
        }

        case 'leader': {
            const tip = entity.data.tip as [number, number, number];
            const land = entity.data.landing as [number, number, number];
            if (!tip || !land) return null;
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <Line points={[tip, land]} color={color} lineWidth={1.5} />
                    <Text position={[land[0] + 0.15, land[1], land[2]]} fontSize={0.25} color={color}>
                        {String(entity.data.text ?? '')}
                    </Text>
                </group>
            );
        }

        case 'table': {
            // 2D-style table: grid lines + per-cell text. Double-click
            // any cell opens the shared text editor on a synthetic mtext?
            // (kept simple: cells render; editing via TABLEEDIT/DDEDIT).
            const cells = (entity.data.cells as string[][]) ?? [];
            const pos = (entity.data.position as [number, number, number]) ?? [0, 0, 0];
            const cw = (entity.data.colWidth as number) || 2;
            const rh = (entity.data.rowHeight as number) || 0.6;
            if (cells.length === 0) return null;
            const cols = cells.reduce((m, r) => Math.max(m, r.length), 0);
            const w = cols * cw, h = cells.length * rh;
            const gridLines: { pts: [number, number, number][] }[] = [];
            for (let r = 0; r <= cells.length; r++) {
                gridLines.push({ pts: [[pos[0], pos[1] - r * rh, pos[2]], [pos[0] + w, pos[1] - r * rh, pos[2]]] });
            }
            for (let c = 0; c <= cols; c++) {
                gridLines.push({ pts: [[pos[0] + c * cw, pos[1], pos[2]], [pos[0] + c * cw, pos[1] - h, pos[2]]] });
            }
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    {gridLines.map((g, i) => (
                        <Line key={i} points={g.pts} color={color} lineWidth={1} />
                    ))}
                    {cells.map((row, r) => row.map((cell, c) => (
                        <Text
                            key={`${r}-${c}`}
                            position={[pos[0] + c * cw + 0.1, pos[1] - r * rh - rh * 0.6, pos[2] + 0.01]}
                            fontSize={rh * 0.5}
                            color={r === 0 && entity.data.hasHeader ? color : color}
                        >
                            {String(cell ?? '')}
                        </Text>
                    )))}
                </group>
            );
        }

        case 'attdef':
            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}
                    position={(entity.data.position as [number, number, number]) || [0, 0, 0]}>
                    <Text fontSize={0.22} color={color} rotation={[0, 0, 0]}>
                        {`<${String(entity.data.tag ?? 'TAG')}>`}
                    </Text>
                </group>
            );

        // --- SOLID / MESH results (command parity: primitives, CSG, surfaces) ---
        case 'solid':
            return (
                <SolidEntityMesh
                    key={entity.id}
                    entity={entity}
                    isSelected={isSelected}
                    isHovered={isHovered}
                />
            );

        // --- NURBS KERNEL citizens (R1): parametric source of truth ---
        case 'nurbs-curve':
            return (
                <NurbsCurveEntity
                    key={entity.id}
                    entity={entity}
                    isSelected={isSelected}
                    isHovered={isHovered}
                />
            );

        case 'nurbs-surface':
            return (
                <NurbsSurfaceEntity
                    key={entity.id}
                    entity={entity}
                    isSelected={isSelected}
                    isHovered={isHovered}
                />
            );

        case 'nurbs-polysurf':
            return (
                <NurbsPolysurfEntity
                    key={entity.id}
                    entity={entity}
                    isSelected={isSelected}
                    isHovered={isHovered}
                />
            );

        case 'subd':
            return (
                <SubdEntityMesh
                    key={entity.id}
                    entity={entity}
                    isSelected={isSelected}
                    isHovered={isHovered}
                />
            );

        // --- NURBS ---
        case 'curve': {
            const controlPoints = (entity.data.controlPoints as number[][]) || [];
            if (controlPoints.length < 2) return null;

            return (
                <group key={entity.id} onClick={highlightClick(entity.id)}>
                    <Line
                        points={curvePoints}
                        color={isSelected || isHovered ? color : '#00ff00'}
                        {...lineStyleProps(style)}
                        lineWidth={isSelected ? 3 : style.width}
                    />
                    {isSelected && controlPoints.map((p, i) => (
                        <mesh key={i} position={[p[0], p[1], p[2]]}>
                            <sphereGeometry args={[0.05, 8, 8]} />
                            <meshBasicMaterial color="#ffff00" />
                        </mesh>
                    ))}
                </group>
            );
        }

        case 'loft':
            return (
                <LoftMesh
                    key={entity.id}
                    data={entity.data as unknown as LoftData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={highlightClick(entity.id)}
                />
            );

        case 'extrude':
            return (
                <ExtrudeMesh
                    key={entity.id}
                    data={entity.data as unknown as ExtrudeData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={highlightClick(entity.id)}
                />
            );

        case 'sweep':
            return (
                <SweepMesh
                    key={entity.id}
                    data={entity.data as unknown as SweepData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={highlightClick(entity.id)}
                />
            );

        case 'revolve':
            return (
                <RevolveMesh
                    key={entity.id}
                    data={entity.data as unknown as RevolveData}
                    isSelected={isSelected}
                    isHovered={isHovered}
                    onClick={highlightClick(entity.id)}
                />
            );

        default:
            return null;
    }
}

/** Build a THREE.Shape from a closed world ring (Z flattened, XY outline). */
function shapeFromPoints(ring: THREE.Vector3[]): THREE.Shape {
    const shape = new THREE.Shape();
    ring.forEach((p, i) => (i === 0 ? shape.moveTo(p.x, p.y) : shape.lineTo(p.x, p.y)));
    shape.closePath();
    return shape;
}
